/**
 * Gleichmäßiges Auto-Scrollen: Geschwindigkeit = Seitenhöhe / Sekunden pro Seite.
 * Sub-Pixel-Akkumulator gegen Ruckeln; Berührung pausiert kurz (manuelles Übersteuern).
 */
const RESUME_AFTER_MS = 900;

export class AutoScroller {
  private frame = 0;
  private last = 0;
  private carry = 0;
  private pausedUntil = 0;
  private touching = false;

  constructor(
    private readonly scroller: HTMLElement,
    private readonly pageHeight: () => number,
    private readonly secsPerPage: () => number,
    private readonly onEnd: () => void,
  ) {}

  get running(): boolean {
    return this.frame !== 0;
  }

  start(): void {
    if (this.frame) return;
    this.last = performance.now();
    this.carry = 0;
    this.scroller.addEventListener("pointerdown", this.onDown, { passive: true });
    window.addEventListener("pointerup", this.onUp, { passive: true });
    window.addEventListener("pointercancel", this.onUp, { passive: true });
    this.scroller.addEventListener("wheel", this.onWheel, { passive: true });
    this.frame = requestAnimationFrame(this.tick);
  }

  stop(): void {
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = 0;
    this.scroller.removeEventListener("pointerdown", this.onDown);
    window.removeEventListener("pointerup", this.onUp);
    window.removeEventListener("pointercancel", this.onUp);
    this.scroller.removeEventListener("wheel", this.onWheel);
  }

  private tick = (now: number) => {
    const dt = Math.min(100, now - this.last);
    this.last = now;
    if (!this.touching && now >= this.pausedUntil) {
      const speed = this.pageHeight() / Math.max(1, this.secsPerPage()); // px pro Sekunde
      this.carry += (speed * dt) / 1000;
      const step = Math.floor(this.carry);
      if (step >= 1) {
        this.carry -= step;
        this.scroller.scrollTop += step;
      }
      const end = this.scroller.scrollHeight - this.scroller.clientHeight;
      if (this.scroller.scrollTop >= end - 1) {
        this.stop();
        this.onEnd();
        return;
      }
    }
    this.frame = requestAnimationFrame(this.tick);
  };

  private onDown = () => {
    this.touching = true;
  };
  private onUp = () => {
    if (!this.touching) return;
    this.touching = false;
    this.pausedUntil = performance.now() + RESUME_AFTER_MS;
  };
  private onWheel = () => {
    this.pausedUntil = performance.now() + RESUME_AFTER_MS;
  };
}
